import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import {
  resolveTenantFromUrl,
  getTenantBasePath,
  isClientGameEnabled,
} from '@low6-pickem/frontend-shared';
import { ClientGameGate } from '@low6-pickem/ui';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OAuthCallback } from './components/auth/OAuthCallback';
import { apiClient } from './api/api-client';
import LobbyView from './views/LobbyView';
import NotFoundView from './views/NotFoundView';

const TENANT_SLUG = resolveTenantFromUrl();
const TENANT_BASE_PATH = getTenantBasePath();

function AppContent() {
  const { isLoading } = useAuth();
  const isOAuthCallback = window.location.pathname.endsWith('/auth/callback');

  useEffect(() => {
    if (isOAuthCallback || isLoading) return;
    apiClient.get('/api/auth/user-details').catch(() => {
      // 401 is handled by the api-client interceptor (triggers OAuth)
    });
  }, [isOAuthCallback, isLoading]);

  return (
    <BrowserRouter basename={TENANT_BASE_PATH || undefined}>
      <Routes>
        {/* More specific view routes before the bare lobby slug */}
        <Route path="/lobby/:lobbySlug/mygames" element={<LobbyView tenantSlug={TENANT_SLUG} />} />
        <Route path="/lobby/:lobbySlug" element={<LobbyView tenantSlug={TENANT_SLUG} />} />
        {/*
          DK's deep-link path (DKPLAT-467): `/lobby?tenantId=<code>&gameId=<lobby guid>`, one
          host with a segment per game, configured on DK's side and not changeable.
          `/lobby` with NO slug matched nothing here and fell through to the catch-all, so the
          link 404'd before any of the id handling could run. The other three games work because
          each mounts its own segment route; this is the lobby's.

          Depending on DK's proxy the app receives either the segment or a stripped root, so both
          `/lobby` and `/` resolve — and both read `?gameId=`.
        */}
        <Route path="/lobby" element={<LobbyView tenantSlug={TENANT_SLUG} />} />
        <Route path="/mygames" element={<LobbyView tenantSlug={TENANT_SLUG} />} />
        <Route path="/" element={<LobbyView tenantSlug={TENANT_SLUG} />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  // Layer A: don't register the lobby if the client didn't deploy it.
  return (
    <ClientGameGate enabled={isClientGameEnabled('lobby')}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ClientGameGate>
  );
}
